import request from '@/config/axios'
import { fetchEventSource } from '@microsoft/fetch-event-source'
import { getAccessToken } from '@/utils/auth'
import { config } from '@/config/axios/config'

// Agent Team 运行 VO
export interface AiTeamRunVO {
  id: number
  templateCode: string
  request: string
  userId: number
  agentId?: number
  supervisorEmployeeId?: number
  employeeRevision?: number
  executionId?: string
  model?: string
  sessionId?: number
  status: string
  budget?: string
  finalOutput?: string
  version: number
  errorMessage?: string
  startedTime?: Date
  completedTime?: Date
  createTime?: Date
}

// Agent Team 运行步骤 VO
export interface AiTeamRunStepVO {
  id: number
  runId: number
  stepKey: string
  stepTitle?: string
  objective?: string
  assignedEmployeeId?: number
  assignedAgentId?: number
  routingScore?: number
  matchedCapabilities?: string[]
  executorType: string
  dependsOn?: string[]
  input?: string
  executionPayload?: Record<string, any>
  output?: string
  status: string
  maxAttempts: number
  timeoutSeconds: number
  idempotencyKey: string
  sortOrder: number
}

export interface AiAutoGenTeamVO {
  id?: number
  teamCode: string
  teamName: string
  description?: string
  participantEmployeeIds: number[]
  terminationText: string
  maxTurns: number
  status: number
  sort: number
}

// Agent Team API
export const TeamRunApi = {
  getTeamList: async () => request.get({ url: '/ai/autogen-team/list' }),
  createTeam: async (data: AiAutoGenTeamVO) => request.post({ url: '/ai/autogen-team/create', data }),
  updateTeam: async (data: AiAutoGenTeamVO) => request.put({ url: '/ai/autogen-team/update', data }),
  deleteTeam: async (id: number) => request.delete({ url: `/ai/autogen-team/delete?id=${id}` }),
  // 生成计划预览
  preview: async (data: { request: string; teamCode?: string; taskType?: string; budget?: string }) => {
    return await request.post({ url: '/ai/team-run/preview', data })
  },

  // 启动运行
  start: async (data: any) => {
    return await request.post({ url: '/ai/team-run', data })
  },

  // 获得运行详情
  getDetail: async (id: number) => {
    return await request.get({ url: `/ai/team-run/${id}` })
  },

  // 运行列表
  getList: async (limit = 20) => {
    return await request.get({ url: `/ai/team-run?limit=${limit}` })
  },

  // 订阅 SSE 事件流
  streamEvents: async (id: number, lastEventId: number | null, handlers: {
    onEvent?: (event: any) => void
    onError?: (err: any) => void
    onClose?: () => void
  }) => {
    const token = getAccessToken()
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`
    }
    if (lastEventId) {
      headers['Last-Event-ID'] = String(lastEventId)
    }
    return fetchEventSource(`${config.base_url}/ai/team-run/${id}/events/stream`, {
      method: 'get',
      headers,
      openWhenHidden: true,
      onmessage: (ev) => {
        if (ev.event === 'event' || ev.event === 'message') {
          try {
            const data = JSON.parse(ev.data)
            handlers.onEvent?.(data)
          } catch (e) {
            // ignore parse error
          }
        }
      },
      onerror: (err) => {
        handlers.onError?.(err)
      },
      onclose: () => {
        handlers.onClose?.()
      }
    })
  },

  // 取消运行
  cancel: async (id: number) => {
    return await request.put({ url: `/ai/team-run/${id}/cancel` })
  },

  // 恢复运行
  resume: async (id: number) => {
    return await request.put({ url: `/ai/team-run/${id}/resume` })
  },

  // 审批收件箱
  getApprovals: async (status = 'PENDING', limit = 50) => {
    return await request.get({ url: `/ai/team-run/approvals?status=${status}&limit=${limit}` })
  },

  // 审批决定
  decideApproval: async (id: number, stepId: number, approved: boolean, reason?: string) => {
    return await request.put({
      url: `/ai/team-run/${id}/steps/${stepId}/approval?approved=${approved}&reason=${reason || ''}`
    })
  },

  // 提交岗位步骤用户补充输入（waitUserInput 步骤挂起后）
  submitStepInput: async (id: number, stepId: number, input: string) => {
    return await request.put({
      url: `/ai/team-run/${id}/steps/${stepId}/input?input=${encodeURIComponent(input)}`
    })
  },

  // 提交评价
  submitFeedback: async (id: number, rating: number, disposition: string, comment?: string) => {
    return await request.put({
      url: `/ai/team-run/${id}/feedback?rating=${rating}&disposition=${disposition}&comment=${comment || ''}`
    })
  },

  // 获得评价
  getFeedback: async (id: number) => {
    return await request.get({ url: `/ai/team-run/${id}/feedback` })
  },

  // 告警列表
  getAlerts: async (status?: string, limit = 50) => {
    return await request.get({ url: `/ai/team-run/alerts?status=${status || ''}&limit=${limit}` })
  },

  // 处理告警
  handleAlert: async (id: number, action: string) => {
    return await request.put({ url: `/ai/team-run/alerts/${id}?action=${action}` })
  }
}
